/**
 * apps/automations-portal/src/components/AppShell.tsx
 *
 * Shared top nav for the signed-in areas (admin + client dashboard). Kept
 * deliberately plain — this is a small internal-facing portal, not the
 * consumer product.
 */
import { NavLink, Outlet } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth/AuthProvider';

export function AppShell({ links }: { links: { to: string; label: string }[] }) {
  const { signOut, user } = useAuth();

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-8 py-4">
          <div className="flex items-center gap-8">
            <span className="font-semibold">Hattie Automations</span>
            <nav className="flex items-center gap-1">
              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end
                  className={({ isActive }) =>
                    cn(
                      'rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground',
                      isActive && 'bg-accent text-accent-foreground',
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {user?.email && <span className="text-sm text-muted-foreground">{user.email}</span>}
            <Button type="button" variant="ghost" size="icon" onClick={() => void signOut()} aria-label="Sign out">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
